<template>
  <div class="mss mss-acuarela">
    <div class="mss-head">
      <strong>{{ title }}</strong>
      <span>{{ progressLabel }}</span>
      <div class="mss-bar"><i :style="{ width: progress + '%' }"></i></div>
    </div>
    <div class="viewport">
      <MaterialStreamLoader v-if="showLoader" ref="loader" />
      <div ref="feed" class="feed">
        <article
          v-for="card in cards"
          :key="card.num"
          :data-num="card.num"
          class="card"
          :class="{
            mounted: card.mounted,
            active: card.phase === 'active',
            complete: card.phase === 'complete',
            'draw-card': card.drawCard,
            'mode-reading': card.mode === 'reading',
          }"
        >
          <header class="meta">
            <span class="num">{{ card.num }}</span>
            <span class="chip">{{ card.tag }}</span>
            <span class="chip">{{ card.family && card.family.label }}</span>
          </header>
          <div class="content">
            <p class="prompt">
              {{ card.typed
              }}<i
                v-if="card.typing && card.mode !== 'reading'"
                class="cursor"
              ></i>
            </p>
            <div
              v-if="card.mode === 'reading'"
              class="reading-box"
              :data-reading="card.num"
            >
              <h4 v-if="card.readingTitle" class="reading-title">
                {{ card.readingTitle }}
              </h4>
              <p class="reading-body">
                {{ card.readingBody
                }}<i v-if="card.typing" class="cursor"></i>
              </p>
            </div>
            <div
              v-else-if="card.mode === 'visual'"
              class="component"
              :class="{
                'draw-frame': card.drawFrame,
                'motor-svg': card.fromMotorSvg,
              }"
              :style="{ '--h': (card.sceneHeight || 250) + 'px' }"
            >
              <!-- v-once keeps scene DOM (drawn/show) across parent patches -->
              <div
                class="exercise"
                v-once
                v-html="card.sceneHtml || ''"
              ></div>
            </div>
            <div
              v-else
              class="text-alts"
              :class="{ on: card.altsVisible }"
            >
              <div
                v-for="(alt, ai) in card.alternatives"
                :key="ai"
                class="text-alt"
              >
                <b>{{ String.fromCharCode(65 + ai) }}</b>
                <span>{{ alt }}</span>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialStreamLoader from "./MaterialStreamLoader.vue";
import { smoothScrollFeed } from "@/utils/prekStreamScenes";
import { wait } from "@/utils/prekVisualStream";

export default {
  name: "MaterialStreamCards",
  components: { MaterialStreamLoader },
  props: {
    cards: { type: Array, required: true },
    progress: { type: Number, default: 0 },
    progressLabel: { type: String, default: "Generando…" },
    title: { type: String, default: "Generando material" },
    showLoader: { type: Boolean, default: false },
  },
  methods: {
    async whenLoaderDone() {
      await this.$nextTick();
      let loader = this.$refs.loader;
      if (!loader) {
        await wait(80);
        loader = this.$refs.loader;
      }
      if (loader && loader.whenDone) return loader.whenDone();
      return wait(2700);
    },
    async whenMessagesDone() {
      await this.$nextTick();
      let loader = this.$refs.loader;
      if (!loader) {
        await wait(80);
        loader = this.$refs.loader;
      }
      if (loader && loader.whenMessagesDone) return loader.whenMessagesDone();
      return wait(1900);
    },
    releaseBurst() {
      const loader = this.$refs.loader;
      if (loader && loader.releaseBurst) loader.releaseBurst();
    },
    cardEl(num) {
      const feed = this.$refs.feed;
      if (!feed) return null;
      return feed.querySelector(`.card[data-num="${num}"]`);
    },
    async typePrompt(card, text, isAlive) {
      card.typed = "";
      card.typing = true;
      const full = text || "";
      for (let i = 0; i < full.length; i += 5) {
        if (isAlive && !isAlive()) break;
        card.typed += full.slice(i, i + 5);
        await wait(8);
      }
      card.typing = false;
    },
    async mountShell(card) {
      card.mounted = false;
      await this.$nextTick();
      requestAnimationFrame(() => {
        card.mounted = true;
      });
      await wait(110);
    },
    async scrollToCard(num) {
      await this.$nextTick();
      // Wait a paint so flex/absolute layout has a real feed height (mobile).
      await new Promise((r) => requestAnimationFrame(() => r()));
      const feed = this.$refs.feed;
      const el = this.cardEl(num);
      if (!feed || !el) return;
      // Scroll ONLY inside the feed — never scroll the page/chat.
      // Prefer getBoundingClientRect: offsetTop breaks when the feed is
      // position:absolute (offsetParent ≠ feed) or cards wrap transforms.
      const pad = feed.firstElementChild === el ? 0 : 14;
      const top = Math.max(
        0,
        feed.scrollTop +
          (el.getBoundingClientRect().top - feed.getBoundingClientRect().top) -
          pad
      );
      const max = Math.max(0, feed.scrollHeight - feed.clientHeight);
      await smoothScrollFeed(feed, Math.min(top, max), 480);
    },
    /** Keep the fixed-height reading pane pinned to the latest streamed line. */
    scrollReadingBody(num) {
      const el = this.cardEl(num);
      if (!el) return;
      const box = el.querySelector(".reading-box");
      if (box) box.scrollTop = box.scrollHeight;
    },
    async animateCard(card, token, isAlive) {
      await this.$nextTick();
      const el = this.cardEl(card.num);
      if (!el) return;

      // Reading cards are animated by the parent while the GPT stream lands.
      if (card.mode === "reading") return;

      const cardIndex = this.cards.findIndex((c) => c.num === card.num);
      if (cardIndex > 0) {
        await this.scrollToCard(card.num);
        await wait(55);
      }

      card.phase = "active";
      card.drawCard = true;
      await wait(280);
      await this.typePrompt(card, card.statement, isAlive);
      if (isAlive && !isAlive()) return;

      if (card.mode === "visual") {
        card.drawFrame = true;
        await wait(70);
        const containers = [...el.querySelectorAll(".container-node")];
        const drawPause = Math.max(
          105,
          Math.min(145, 1450 / Math.max(1, containers.length))
        );
        for (const container of containers) {
          if (isAlive && !isAlive()) return;
          container.classList.add("drawn");
          await wait(drawPause);
        }
        // stroke-node lo emite el SVG del motor (trazos). Los builders HTML no
        // lo usan; el querySelectorAll vacío deja esta etapa en no-op.
        const strokes = [...el.querySelectorAll(".stroke-node")];
        for (const stroke of strokes) {
          if (isAlive && !isAlive()) return;
          stroke.classList.add("traced");
          await wait(
            Math.max(8, Math.min(40, 500 / Math.max(1, strokes.length)))
          );
        }
        const measure = el.querySelector(".measure-board");
        if (measure) {
          measure.classList.add("line-ready");
          await wait(260);
        }
        const nodes = [...el.querySelectorAll(".node")];
        for (const n of nodes) {
          if (isAlive && !isAlive()) return;
          n.classList.add("show");
          await wait(Math.max(18, Math.min(45, 360 / Math.max(1, nodes.length))));
        }
        const assets = [...el.querySelectorAll(".asset-node")];
        for (const asset of assets) {
          if (isAlive && !isAlive()) return;
          asset.classList.add("show");
          await wait(
            Math.max(22, Math.min(52, 460 / Math.max(1, assets.length)))
          );
        }
        await wait(90);
      } else {
        card.altsVisible = true;
        await wait(520);
      }

      card.phase = "complete";
      card.drawCard = false;
      await wait(620);
    },
  },
};
</script>

<style scoped>
.mss {
  --pink: var(--main-color, #e84855);
  --pink2: var(--secondary-color, #bf2f3d);
  --deep: var(--product-deep, #52141c);
  --gold: #e8c56a;
  --ink: #1a2332;
  --muted: #6d7788;
  --line: var(--product-line, #ecd6da);
  --soft: var(--product-soft, #fff1f3);
  --rgb: var(--product-rgb, 232, 72, 85);
  --accent: var(--pink2);
  font-family: Ubuntu, "Segoe UI", system-ui, sans-serif;
  color: var(--ink);
}
.mss-head {
  display: grid;
  gap: 6px;
  margin-bottom: 10px;
  flex-shrink: 0;
}
.mss-head strong {
  font-size: 1rem;
}
.mss-head span {
  font-size: 0.84rem;
  color: var(--muted);
}
.mss-bar {
  height: 6px;
  border-radius: 999px;
  background: rgba(var(--rgb), 0.12);
  overflow: hidden;
}
.mss-bar i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--pink), color-mix(in srgb, var(--pink) 55%, white));
  border-radius: inherit;
  transition: width 0.25s ease;
}
.viewport {
  /* Bounded box: feed scrolls inside. height:auto made the feed grow with
     content (esp. on mobile), so scrollTop did nothing and you stayed on #1. */
  position: relative;
  flex: 1 1 auto;
  min-height: 280px;
  min-width: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(var(--rgb), 0.18);
  border-radius: 28px;
  box-shadow: 0 28px 80px rgba(var(--rgb), 0.1);
  backdrop-filter: blur(16px);
}
.feed {
  position: absolute;
  inset: 0;
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
  padding: clamp(14px, 2.2vw, 24px);
  display: flex;
  flex-direction: column;
  gap: 24px;
  scroll-behavior: auto;
}
.feed::-webkit-scrollbar {
  width: 9px;
}
.feed::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, var(--pink) 35%, #e8e0e4);
  border-radius: 99px;
  border: 2px solid #fff;
}
.card {
  position: relative;
  flex: none;
  width: 100%;
  border-radius: 22px;
  background: #fff;
  opacity: 0;
  transform: translateY(24px) scale(0.965);
  filter: blur(6px);
  box-shadow: 0 12px 32px rgba(var(--rgb), 0.07);
  overflow: hidden;
  scroll-margin-top: 16px;
}
.card.mounted {
  opacity: 1;
  transform: none;
  filter: none;
  transition: opacity 0.42s ease, filter 0.58s ease,
    transform 0.7s cubic-bezier(0.16, 1.18, 0.3, 1);
}
.card.active {
  box-shadow: 0 18px 44px rgba(var(--rgb), 0.13);
}
.card:before {
  content: "";
  position: absolute;
  inset: -62% -48%;
  z-index: 12;
  pointer-events: none;
  background: linear-gradient(
    112deg,
    transparent 32%,
    rgba(255, 255, 255, 0.04) 39%,
    rgba(255, 255, 255, 0.38) 45%,
    rgba(248, 252, 255, 0.92) 49%,
    rgba(255, 247, 219, 0.55) 52%,
    rgba(238, 247, 255, 0.72) 55%,
    rgba(255, 255, 255, 0.22) 61%,
    transparent 69%
  );
  mix-blend-mode: screen;
  transform: translateX(-82%) rotate(3deg);
  opacity: 0;
  will-change: transform, opacity;
}
.card.complete {
  animation: cardReady 1.75s cubic-bezier(0.22, 0.72, 0.24, 1) both;
}
.card.complete:before {
  animation: readyShine 1.65s cubic-bezier(0.35, 0.02, 0.18, 1) 0.08s both;
}
.card.complete .num {
  animation: numReady 1.15s cubic-bezier(0.22, 0.8, 0.25, 1);
}
.card:after {
  content: "";
  position: absolute;
  inset: 0;
  border: 1.6px solid var(--pink);
  border-radius: inherit;
  pointer-events: none;
  z-index: 8;
  clip-path: inset(0 100% 0 0);
}
.card.draw-card:after {
  animation: borderSweep 0.72s cubic-bezier(0.55, 0.05, 0.25, 1) forwards;
}
.meta {
  min-height: 50px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px dashed rgba(var(--rgb), 0.22);
  background: linear-gradient(180deg, #fff, color-mix(in srgb, var(--pink) 4%, #fff));
}
.num,
.chip {
  display: inline-grid;
  place-items: center;
  font-weight: 900;
  color: var(--pink2);
  border: 1px dashed rgba(var(--rgb), 0.32);
  background: color-mix(in srgb, var(--pink) 5%, #fff);
}
.num {
  width: 31px;
  height: 31px;
  border-radius: 11px;
}
.chip {
  min-height: 27px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
}
.content {
  padding: 14px 16px 17px;
}
.prompt {
  min-height: 42px;
  margin: 0 0 10px;
  font-size: 0.92rem;
  font-weight: 850;
  line-height: 1.42;
}
/* Fixed pane: text streams inside; card shell does not grow. */
.reading-box {
  height: 220px;
  min-height: 220px;
  max-height: 220px;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--pink) 3%, #fff),
    #fff
  );
  scrollbar-gutter: stable;
}
.reading-box::-webkit-scrollbar {
  width: 7px;
}
.reading-box::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, var(--pink) 30%, #e8e0e4);
  border-radius: 99px;
}
.reading-title {
  margin: 0 0 8px;
  font-size: 0.95rem;
  font-weight: 900;
  color: var(--deep);
  line-height: 1.3;
}
.reading-body {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1.55;
  color: var(--ink);
  white-space: pre-wrap;
}
.card.mode-reading .prompt {
  min-height: 28px;
  margin-bottom: 8px;
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--muted);
}
.card.mode-reading .num {
  width: auto;
  min-width: 31px;
  padding: 0 7px;
  font-size: 0.72rem;
}
.component {
  position: relative;
  height: var(--h, 250px);
  min-height: var(--h, 250px);
  max-height: var(--h, 250px);
  padding: 14px;
  border-radius: 17px;
  background: #fff;
  overflow: hidden;
  flex: none;
}
.exercise {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.text-alts {
  display: grid;
  gap: 8px;
  opacity: 0;
  transform: translateY(6px);
  transition: 0.35s ease;
}
.text-alts.on {
  opacity: 1;
  transform: none;
}
.text-alt {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 8px;
  align-items: start;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 8px 10px;
  background: #fff;
  font-size: 0.84rem;
}
.text-alt b {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--soft);
  color: var(--pink);
  display: grid;
  place-items: center;
  font-size: 0.75rem;
}
@keyframes borderSweep {
  to {
    clip-path: inset(0);
  }
}
@keyframes readyShine {
  0% {
    opacity: 0;
    transform: translateX(-82%) rotate(3deg);
  }
  16% {
    opacity: 0.3;
  }
  36% {
    opacity: 0.92;
  }
  68% {
    opacity: 0.72;
  }
  100% {
    opacity: 0;
    transform: translateX(82%) rotate(3deg);
  }
}
@keyframes cardReady {
  0% {
    transform: translateY(0) perspective(900px) rotateX(0);
    box-shadow: 0 18px 44px rgba(var(--rgb), 0.13);
  }
  40% {
    transform: translateY(-1px) perspective(900px) rotateX(0.22deg);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.82),
      0 18px 42px rgba(82, 20, 28, 0.1);
  }
  100% {
    transform: translateY(0) perspective(900px) rotateX(0);
    box-shadow: 0 12px 32px rgba(82, 20, 28, 0.075);
  }
}
@keyframes numReady {
  0%,
  100% {
    transform: scale(1);
  }
  45% {
    transform: scale(1.07);
    box-shadow: 0 0 0 4px rgba(232, 197, 106, 0.1);
  }
}
@media (max-width: 650px) {
  .viewport {
    border: 0;
    border-radius: 16px;
  }
  .feed {
    padding: 11px;
  }
  .component {
    height: calc(var(--h, 250px) * 0.9);
    min-height: calc(var(--h, 250px) * 0.9);
    max-height: calc(var(--h, 250px) * 0.9);
  }
  .chip:nth-of-type(3) {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  *,
  *:before,
  *:after {
    animation-duration: 0.01ms !important;
    animation-delay: 0ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>

<!-- Exercise DOM from v-html — unscoped under .mss-acuarela -->
<style>
.mss-acuarela .cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: -0.1em;
  background: var(--pink);
  animation: mss-blink 0.75s step-end infinite;
}
@keyframes mss-blink {
  50% {
    opacity: 0;
  }
}
.mss-acuarela .node,
.mss-acuarela .asset-node {
  opacity: 0;
  transform: translateY(10px) scale(0.9);
  filter: blur(2px);
}
.mss-acuarela .node.show,
.mss-acuarela .asset-node.show {
  opacity: 1;
  transform: none;
  filter: none;
  transition: opacity 0.3s ease, filter 0.34s ease,
    transform 0.58s cubic-bezier(0.18, 1.45, 0.36, 1);
}
/* SVG del motor: mismas clases, sin el :after de las cajas HTML */
.mss-acuarela .motor-svg svg {
  display: block;
  width: 100%;
  height: 100%;
}
.mss-acuarela .motor-svg .container-node {
  opacity: 0;
}
.mss-acuarela .motor-svg .container-node.drawn {
  opacity: 1;
  transition: opacity 0.18s ease;
}
.mss-acuarela .motor-svg .container-node:after {
  content: none;
}
.mss-acuarela .motor-svg .container-node > * {
  visibility: visible;
}
.mss-acuarela .motor-svg .stroke-node {
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  transition: stroke-dashoffset 0.45s ease;
}
.mss-acuarela .motor-svg .stroke-node.traced {
  stroke-dashoffset: 0;
}
.mss-acuarela .motor-svg .node,
.mss-acuarela .motor-svg .asset-node {
  transform: none;
  filter: none;
}
.mss-acuarela .asset {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 5px 4px rgba(26, 35, 50, 0.08));
}
.mss-acuarela .asset-missing {
  display: block;
  width: 100%;
  height: 48px;
  background: repeating-linear-gradient(
    -45deg,
    color-mix(in srgb, var(--pink) 14%, #fff),
    color-mix(in srgb, var(--pink) 14%, #fff) 6px,
    #fff 6px,
    #fff 12px
  );
  border-radius: 8px;
}
.mss-acuarela .box {
  position: relative;
  border: 0;
  border-radius: 14px;
  background: #fff;
  min-width: 0;
  overflow: hidden;
}
.mss-acuarela .mini-frame {
  display: none;
}
.mss-acuarela .container-node:after {
  content: "";
  position: absolute;
  inset: 0;
  border: 1.6px solid var(--pink);
  border-radius: inherit;
  pointer-events: none;
  z-index: 8;
  clip-path: inset(0 100% 0 0);
}
.mss-acuarela .container-node.drawn:after {
  animation: mss-borderSweep 0.14s cubic-bezier(0.45, 0.05, 0.22, 1) forwards;
}
@keyframes mss-borderSweep {
  to {
    clip-path: inset(0);
  }
}
.mss-acuarela .container-node > *:not(.mini-frame) {
  visibility: hidden;
  position: relative;
  z-index: 2;
}
.mss-acuarela .container-node.drawn > *:not(.mini-frame) {
  visibility: visible;
}
/* Scene spatial: absolute placement must beat container-node position:relative */
.mss-acuarela .scene-main.container-node > .pink-zone,
.mss-acuarela .scene-main.container-node > img,
.mss-acuarela .scene-main.container-node > .asset-node {
  position: absolute;
}
.mss-acuarela .radio {
  width: 18px !important;
  height: 18px !important;
  min-width: 18px;
  min-height: 18px;
  max-width: 18px;
  max-height: 18px;
  border: 2px solid var(--accent);
  border-radius: 50%;
  display: block;
  margin: 6px auto 0;
  flex: none;
  align-self: center;
  box-sizing: border-box;
  aspect-ratio: 1 / 1;
  background: #fff;
  overflow: visible;
  transform: none !important;
}
.mss-acuarela .seq {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}
.mss-acuarela .seq .box {
  padding: 6px;
}
.mss-acuarela .matching {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(4, 1fr);
  gap: 8px 24px;
}
.mss-acuarela .match {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 12px;
}
.mss-acuarela .match img {
  width: 54px;
  height: 48px;
  object-fit: contain;
}
.mss-acuarela .match b {
  font-size: 0.75rem;
}
.mss-acuarela .scene {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1.3fr repeat(2, 0.68fr);
  grid-template-rows: 1fr 1fr;
  gap: 9px;
}
.mss-acuarela .scene-main {
  grid-row: 1 / 3;
  position: relative;
  min-height: 0;
  background: #fff;
}
.mss-acuarela .scene-main img,
.mss-acuarela .scene-main .asset-node {
  position: absolute;
  width: 23%;
  height: 23%;
  object-fit: contain;
}
.mss-acuarela .scene-main .a {
  left: 7%;
  top: 7%;
}
.mss-acuarela .scene-main .b {
  right: 8%;
  top: 7%;
}
.mss-acuarela .scene-main .c {
  left: 34%;
  top: 32%;
  width: 33%;
  height: 33%;
}
.mss-acuarela .scene-main .d {
  right: 8%;
  bottom: 7%;
}
/* Pink zone = “recuadro rosa” (NT spatial inside) */
.mss-acuarela .scene-main .pink-zone {
  position: absolute;
  left: 26%;
  top: 22%;
  width: 48%;
  height: 56%;
  border-radius: 18px;
  background: color-mix(in srgb, var(--pink) 8%, rgba(255, 255, 255, 0.92));
  border: 2.5px solid rgba(var(--rgb), 0.55);
  display: grid;
  place-items: center;
  z-index: 3;
  box-sizing: border-box;
}
.mss-acuarela .scene-main .pink-zone .inside,
.mss-acuarela .scene-main .pink-zone img {
  position: static !important;
  width: 72% !important;
  height: 72% !important;
  object-fit: contain;
}
.mss-acuarela .scene-main .corner {
  width: 28%;
  height: 28%;
  z-index: 2;
}
.mss-acuarela .scene-main .corner.tl {
  left: 4%;
  top: 4%;
}
.mss-acuarela .scene-main .corner.tr {
  right: 4%;
  top: 4%;
  left: auto;
}
.mss-acuarela .scene-main .corner.br {
  right: 4%;
  bottom: 4%;
  left: auto;
  top: auto;
}
.mss-acuarela .scene-main .corner.bl {
  left: 4%;
  bottom: 4%;
  top: auto;
}
.mss-acuarela .scene.fallback-png .scene-main img {
  position: static;
  width: 100%;
  height: 100%;
}
.mss-acuarela .choice {
  padding: 7px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 0;
}
.mss-acuarela .choice > img,
.mss-acuarela .choice > .asset-node {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  max-height: calc(100% - 28px);
  object-fit: contain;
}
.mss-acuarela .choice > .radio {
  flex: 0 0 18px;
}
.mss-acuarela .three {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.mss-acuarela .choice-letter {
  position: absolute;
  left: 8px;
  top: 6px;
  font-size: 0.72rem;
  font-weight: 950;
}
.mss-acuarela .choice-card {
  position: relative;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 0;
}
.mss-acuarela .choice-card > .radio {
  flex: 0 0 18px;
  margin-top: 6px;
}
.mss-acuarela .objects {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  place-items: center;
  gap: 2px;
  flex: 1;
}
.mss-acuarela .objects img {
  width: 100%;
  height: 48px;
  object-fit: contain;
}
.mss-acuarela .frequency .choice-card {
  padding: 9px 9px 7px;
}
.mss-acuarela .frequency img {
  height: 68%;
  object-fit: contain;
}
.mss-acuarela .frequency b {
  text-align: center;
  font-size: 0.75rem;
  margin-top: 2px;
}
.mss-acuarela .count {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 14px;
}
.mss-acuarela .big-number {
  display: grid;
  place-items: center;
  font-size: 4.2rem;
  font-weight: 950;
  color: var(--accent);
}
.mss-acuarela .big-number.write-box {
  min-height: 100%;
  background: #fff;
}
.mss-acuarela .big-number.write-box .write-slot {
  display: block;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
}
.mss-acuarela .bones {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 5px;
}
.mss-acuarela .bones img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.mss-acuarela .equation {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 28px 1fr 28px 0.6fr;
  align-items: center;
  gap: 8px;
}
.mss-acuarela .group,
.mss-acuarela .answer {
  height: 82%;
  padding: 8px;
}
.mss-acuarela .group {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  place-items: center;
}
.mss-acuarela .group.three-items {
  grid-template-columns: repeat(3, 1fr);
}
.mss-acuarela .group img {
  width: 100%;
  height: 52px;
  object-fit: contain;
}
.mss-acuarela .op {
  font-size: 1.55rem;
  font-weight: 950;
  text-align: center;
}
.mss-acuarela .views {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1.15fr repeat(3, 0.72fr);
  gap: 8px;
}
.mss-acuarela .view {
  padding: 8px;
  display: grid;
  grid-template-rows: 1fr auto auto;
  min-width: 0;
}
.mss-acuarela .view img {
  min-height: 0;
}
.mss-acuarela .view b {
  text-align: center;
  font-size: 0.67rem;
  margin-top: 3px;
}
.mss-acuarela .shape {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 0.88fr 1.8fr;
  gap: 12px;
}
.mss-acuarela .shape-main {
  padding: 9px;
}
.mss-acuarela .questions {
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  gap: 8px;
}
.mss-acuarela .question-block {
  position: relative;
  padding: 24px 7px 7px;
  border-radius: 11px;
}
.mss-acuarela .question-title {
  position: absolute !important;
  left: 10px;
  top: 6px;
  font-size: 0.68rem;
  font-weight: 900;
  color: #1a2332;
}
.mss-acuarela .qrow {
  height: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.mss-acuarela .qrow .box {
  display: grid;
  place-items: center;
  font-size: 0.7rem;
  font-weight: 850;
}
.mss-acuarela .measure {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: 1.35fr 0.65fr;
  gap: 10px;
}
.mss-acuarela .measure-board {
  position: relative;
  width: 82%;
  justify-self: center;
  padding: 6px 12px 42px;
  display: grid;
  grid-template-rows: 1fr auto;
  align-items: end;
  gap: 4px;
}
.mss-acuarela .measure-board:before {
  content: "";
  position: absolute;
  left: 6%;
  right: 6%;
  bottom: 36px;
  height: 3px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left;
  z-index: 1;
}
.mss-acuarela .measure-board.line-ready:before {
  transform: scaleX(1);
  transition: transform 0.55s ease;
}
.mss-acuarela .pencil {
  width: 88%;
  height: 100%;
  max-height: 120px;
  margin: auto;
  object-fit: contain;
}
.mss-acuarela .measure-units {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: flex-end;
  width: 92%;
  margin: 0 auto;
  min-height: 44px;
  gap: 2px;
}
.mss-acuarela .measure-units .measure-unit {
  flex: 1 1 0;
  width: auto;
  max-width: 48px;
  height: 42px;
  object-fit: contain;
}
.mss-acuarela .hands {
  width: 78%;
  justify-self: center;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  align-self: center;
}
.mss-acuarela .hand {
  display: grid;
  grid-template-rows: 1fr auto;
  place-items: center;
  padding: 8px 4px 6px;
  min-height: 48px;
  background: linear-gradient(180deg, #fff 0%, #fff 76%, color-mix(in srgb, var(--pink) 6%, #fff) 76%);
}
.mss-acuarela .hand b {
  display: grid;
  place-items: center;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 900;
  color: var(--deep);
}
.mss-acuarela .hand .radio {
  margin-top: 4px;
}
.mss-acuarela .hand.write-slot {
  width: 42%;
  justify-self: center;
  min-height: 56px;
  grid-template-rows: auto auto;
  gap: 6px;
}
.mss-acuarela .write-blank {
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: 0.2em;
  color: var(--accent);
}
.mss-acuarela .story {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 34px 1fr;
  grid-template-rows: 0.78fr 1.22fr;
  gap: 11px;
}
.mss-acuarela .story-panel {
  padding: 9px 13px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  place-items: center;
}
.mss-acuarela .story-panel.two {
  grid-template-columns: repeat(2, 1fr);
}
.mss-acuarela .story-panel img {
  width: 100%;
  height: 72px;
  object-fit: contain;
}
.mss-acuarela .steps {
  grid-column: 1 / 4;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.mss-acuarela .step {
  position: relative;
  padding: 31px 9px 9px;
  font-size: 0.67rem;
  font-weight: 800;
  background: #fff;
}
.mss-acuarela .step-title {
  position: absolute !important;
  top: 9px;
  left: 10px;
  right: 8px;
  white-space: nowrap;
  color: var(--deep);
}
.mss-acuarela .step-options {
  height: 100%;
  display: grid;
  align-content: stretch;
  gap: 6px;
}
.mss-acuarela .step-option {
  display: flex;
  align-items: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  padding: 6px 8px;
  background: color-mix(in srgb, var(--pink) 6%, #fff);
  font-size: 0.63rem;
  line-height: 1.2;
}
.mss-acuarela .step-option:before {
  content: "";
  width: 11px;
  height: 11px;
  border: 1.5px solid var(--accent);
  border-radius: 50%;
  flex: none;
}
@media (max-width: 650px) {
  .mss-acuarela .matching {
    gap: 7px;
  }
  .mss-acuarela .match {
    padding: 4px 7px;
  }
  .mss-acuarela .match img {
    width: 38px;
  }
  .mss-acuarela .views {
    grid-template-columns: 1fr repeat(3, 0.68fr);
  }
}
</style>
